import React, { memo, useCallback, useMemo } from 'react';
import { EditorStarterItem } from '../../items/item-type';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import {
  setPositionLeft,
  setPositionTop,
} from '../../state/actions/set-position';
import { getRectAfterCrop } from '../../utils/get-dimensions-after-crop';
import { useItem, useWriteContext } from '../../utils/use-context';
import { InspectorSubLabel } from '../components/inspector-label';
import { useSelectionValues } from '../utils/use-selection-values';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { ControlsPadding } from './controls-padding';
import { NumberControlUpdateHandler } from './number-controls';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';

// 아이템에서 crop 적용 후 left 추출
const getLeftFromItem = (item: EditorStarterItem): number => {
  return Math.floor(getRectAfterCrop(item).left);
};

// 아이템에서 crop 적용 후 top 추출
const getTopFromItem = (item: EditorStarterItem): number => {
  return Math.floor(getRectAfterCrop(item).top);
};

const PositionControlUnmemoized: React.FC<{
  itemId: string;
}> = ({ itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const item = useItem(itemId);

  const targetIds = useTargetItemIds(itemId);
  const { isMixed: isLeftMixed } = useSelectionValues<
    EditorStarterItem,
    number
  >(itemId, getLeftFromItem);
  const { isMixed: isTopMixed } = useSelectionValues<EditorStarterItem, number>(
    itemId,
    getTopFromItem,
  );

  const rectAfterCrop = useMemo(() => {
    if (!item) return { left: 0, top: 0, width: 0, height: 0 };
    return getRectAfterCrop(item);
  }, [item]);

  const onLeft: NumberControlUpdateHandler = useCallback(
    ({ num: leftAfterCrop, commitToUndoStack }) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const currentRectAfterCrop = getRectAfterCrop(i);
            const diff = i.left - currentRectAfterCrop.left;
            const newLeft = leftAfterCrop + diff;

            return setPositionLeft({ item: i, left: newLeft });
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const onTop: NumberControlUpdateHandler = useCallback(
    ({ num: topAfterCrop, commitToUndoStack }) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const currentRectAfterCrop = getRectAfterCrop(i);
            const diff = i.top - currentRectAfterCrop.top;
            const newTop = topAfterCrop + diff;

            return setPositionTop({ item: i, top: newTop });
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  if (!item) return null;

  return (
    <div>
      <InspectorSubLabel>
        {t('Editor.inspector.text.position')}
      </InspectorSubLabel>
      <div className="flex gap-4xs">
        <div className="flex-1">
          <ValueInputField
            isShowLeadingNode={true}
            leadingNodeType="text"
            leadingNodeValue="X"
            setValue={onLeft}
            value={Math.floor(rectAfterCrop.left)}
            min={null}
            max={null}
            step={1}
            accessibilityLabel="X"
            hasMixedValue={isLeftMixed}
          />
        </div>
        <div className="flex-1">
          <ValueInputField
            isShowLeadingNode={true}
            leadingNodeType="text"
            leadingNodeValue="Y"
            setValue={onTop}
            value={Math.floor(rectAfterCrop.top)}
            min={null}
            max={null}
            step={1}
            accessibilityLabel="Y"
            hasMixedValue={isTopMixed}
          />
        </div>
        <ControlsPadding />
      </div>
    </div>
  );
};

export const PositionControl = memo(PositionControlUnmemoized);
